import { Injectable } from '@angular/core';
import { BillingService } from '@galaxy/billing';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, Rule, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import * as BillingParameterApproved from './billing-parameter-approved';
import { BILLING_ITEM_NOT_FOUND } from './billing-purchase-success.constants';

@Injectable({ providedIn: 'root' })
export class BillingPurchaseSuccessService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    private readonly billingService: BillingService,
  ) {
    super();
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    return this.getTemplateDisplayString(step);
  }

  getTemplateNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    return this.getTemplateDisplayString(step);
  }

  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    const data = JSON.parse(activity.triggerData) ?? {};
    const merchantId = data.merchant_id;
    const sku = data.sku;
    const customerId = data.customer_id;
    const orderId = data.order_id;
    const approved = data.approved;

    const billableItem$ = this.billingService.getBillableItem(merchantId, sku, customerId, orderId);
    const displayString$ = billableItem$.pipe(
      switchMap((billableItem) => {
        const key =
          approved === 'true'
            ? 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTIVITY.ACCOUNT_BILLED'
            : 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTIVITY.ACCOUNT_NOT_BILLED';

        return this.translateService.stream(key, {
          accountName: billableItem.customerName,
          productName: billableItem.productName,
        });
      }),
      catchError((err) => {
        let key: string;
        if (err.error.code === BILLING_ITEM_NOT_FOUND) {
          key = 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTIVITY.ITEM_NOT_FOUND';
        } else {
          key = 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTIVITY.UNKNOWN_ERROR';
        }
        return this.translateService.stream(key);
      }),
    );

    return this.triggerActivityEntry(displayString$);
  }

  getTemplateDisplayString(step: WorkflowStepInterface): Observable<NodeData> {
    let key = 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTION.WHEN_BILLING';

    if (step?.choices?.rules) {
      const rules = Rule.fromRuleInterface(step.choices.rules[0]);
      const approvedRule = rules.listValuesByVariablePath(BillingParameterApproved.path());

      if (approvedRule.length === 1 && approvedRule[0] === true) {
        key = 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTION.WHEN_BILLING_SUCCESS';
      } else if (approvedRule.length === 1 && approvedRule[0] === false) {
        key = 'AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.ACTION.WHEN_BILLING_FAIL';
      }
      return this.nodeDisplayData(this.translateService.stream(key), this.noValidationErrorsDetected());
    }
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.BILLING_PURCHASE.CONFIG_ERROR'),
      this.noValidationErrorsDetected(),
    );
  }
}
